import { useEffect, useRef, useState } from 'react'
import { useAuth } from './AuthProvider'
import { ProfileModal } from './ProfileModal'
import { BusinessCard } from './BusinessCard'
import '@/shared/ui/RecordForm.css'

/**
 * The avatar in the top-right, and everything about the session behind it.
 *
 * Who you are signed in as used to sit in a footer strip below the app. That
 * strip was the only thing keeping the interface off the edge of the window,
 * and it spent a full row on information you need roughly once a day — so it
 * moved in here, where you look when you want it.
 */
export function AccountMenu() {
  const { user, isAdmin, logOut } = useAuth()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [card, setCard] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const name = user?.displayName ?? ''
  // Two letters, per the design. A name gives first + last; an address that has
  // never been through the profile editor gives its first letter and nothing to
  // pair it with, which is still better than a blank circle.
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || (user?.email ?? '?').charAt(0).toUpperCase()

  return (
    <div className="account" ref={wrap}>
      <button
        className="account__avatar"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account"
        title={name}
      >
        {user?.photoUrl ? (
          <img className="account__photo" src={user.photoUrl} alt="" />
        ) : (
          initials
        )}
      </button>

      {open && (
        <div className="account__menu" role="menu">
          <div className="account__name">{name || 'Signed in'}</div>
          <div className="account__email">{user?.email}</div>
          <button
            className="account__item"
            role="menuitem"
            onClick={() => {
              setEditing(true)
              setOpen(false)
            }}
          >
            Edit profile
          </button>
          {/* Directly under Edit profile, as the design places it — the two
              are one loop: fill the fields in, then look at what they produce. */}
          <button
            className="account__item"
            role="menuitem"
            onClick={() => {
              setCard(true)
              setOpen(false)
            }}
          >
            Digital business card
          </button>
          <div className="account__role">
            {isAdmin ? 'Admin' : 'Sales'}
            <span className="account__rolenote">
              {isAdmin
                ? 'can edit pipeline stages, templates and delete leads'
                : 'settings are read-only'}
            </span>
          </div>
          <button className="account__signout" onClick={() => void logOut()} role="menuitem">
            Sign out
          </button>
        </div>
      )}

      {editing && <ProfileModal onClose={() => setEditing(false)} />}
      {card && <BusinessCard onClose={() => setCard(false)} />}
    </div>
  )
}
